// src/pages/Help/plotHelp.js
import { React, useLayoutEffect, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { helpSections } from "./data/helpData";
import { generalHelpSections } from "./data/generalHelpData";


// Chapters for the Help Video, this will appear in the chapters section to the right
// of the vid
const HELP_VIDEO_CHAPTERS = [
  { time: "00:00", label: "Plot settings/options" },
  { time: "00:54", label: "Checkbox file selection" },
  { time: "02:23", label: "Drag and drop file selection" },
  { time: "04:44", label: "Interacting with plots" },
  { time: "06:58", label: "Interacting with tables" },
  { time: "08:04", label: "Genome Browser" },
];


// Converts a timestamp like 02:23 to an int val in seconds
const timeToSeconds = (t) => {
  const parts = String(t).trim().split(":").map((x) => Number(x));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1]
  };
  return 0;
};

// modifies a youtube url to include tags like origin, autoplay & enablejsapi which
// is required for seeking to work
const modifyURL = (urlString, startSeconds = null) => {
  try {
    const u = new URL(urlString);
    u.searchParams.set("enablejsapi", "1");
    u.searchParams.set("origin", window.location.origin);
    if (startSeconds !== null) {
      u.searchParams.set("start", String(startSeconds));
      u.searchParams.set("autoplay", "1");
    }
    return u.toString();
  } catch {
    const joiner = urlString.includes("?") ? "&" : "?";
    const startPart =
      startSeconds !== null ? `&start=${startSeconds}&autoplay=1` : "";
    return `${urlString}${joiner}enablejsapi=1${startPart}`;
  }
};

const parseSeekParam = (raw) => {
  if (!raw) {
    return null;
  }

  // allows either seconds: "55" or timestamp: "02:23" or "02:23"
  if (/^\d+$/.test(raw)) return Number(raw);

  const parts = raw.split(":").map((x) => Number(x));
  if (parts.some((n) => Number.isNaN(n))) {
    return null;
  }

  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  return null;
};

const plotHelp = () => {
  const { plotId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const iframeRef = useRef(null);
  const [fallbackStart, setFallbackStart] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Find the plot data - now search in both regular plots and nested subPages
  let plotData = null;
  let parentData = null;

  // First, check regular plots
  plotData = helpSections.plots.find((plot) => plot.id === plotId);

  // If not found, search in nested subPages
  if (!plotData) {
    for (const plot of helpSections.plots) {
      if (plot.isGroup && plot.subPages) {
        const subPage = plot.subPages.find((sub) => sub.id === plotId);
        if (subPage) {
          plotData = subPage;
          parentData = plot; // Store parent for breadcrumb
          break;
        }
      }
    }
  }

  // If still not found check the general help pages
  if (!plotData) {
    plotData = generalHelpSections.helpSectionArray.find((plot) => plot.id === plotId);
  }

  if (!plotData) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h4>plot not found</h4>
            <Button color="primary" onClick={() => navigate("/help")}>
              Back to Help
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  // takes the chapters defined in HELP_VIDEO_CHAPTERS and converts the timestamps
  // to seconds
  const chapters = useMemo(() => {
    const raw = plotId === "help-video" ? HELP_VIDEO_CHAPTERS : [];
    return raw.map((c) => ({ ...c, seconds: timeToSeconds(c.time) }));
  }, [plotId]);

  // Adds tags to the youtube url that allow postmsgs to work
  const videoSrc = useMemo(() => {
    if (!plotData?.videoUrl?.trim()) {
      return "";
    }
    return modifyURL(plotData.videoUrl, fallbackStart);
  }, [plotData?.videoUrl, fallbackStart]);

  // Video specific useEffect: Reset fallback start & iframeloaded when 
  // navigating between help pages
  useEffect(() => {
    setFallbackStart(null);
    setIframeLoaded(false);
  }, [plotId]);

  // called when a user clicks a button in the chapters section to seek to that part of
  // the video & autoplay, also used when a timestamp has been passed in the url which
  // is used in the links from other help pages
  const handleChapterClick = (seconds) => {
    const iframe = iframeRef.current;

    // If iframe isn't mounted yet, fall back to start-param reload.
    if (!iframe || !iframe.contentWindow) {
      setFallbackStart(seconds);
      return;
    }

    // YouTube listens for postMessage commands when enablejsapi=1 is present.
    let targetOrigin = "https://www.youtube.com";

    try {
      const post = (func, args = []) => {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: "command", func, args }),
          targetOrigin
        );
      };
      // Apparently some browsers will refuse to autoplay a video if it's not muted, 
      // that can be done with this line, but so far haven't seen evidence of this
      // so leaving commented out for now, would make sense to enable this if video
      // is not going to be narrated, but haven't decided on that yet
      //post("mute");
      post("seekTo", [seconds, true]);
      post("playVideo");
    } catch (e) {
      // fallback if postMessage fails for any reason
      setFallbackStart(seconds);
    }
  };

  useEffect(() => {
    if (plotId !== "help-video") return;
    if (!iframeLoaded) return;

    const params = new URLSearchParams(location.search);
    const tRaw = params.get("t");
    const seconds = parseSeekParam(tRaw);

    if (seconds == null) return;

    let tries = 0;
    const tick = () => {
      tries += 1;
      handleChapterClick(seconds);

      // player can ignore the first message or two while initializing, this is needed
      // when passing a timestamp in the url params, otherwise will try to initalize
      // too early and fail to seek/autoplay vid
      if (tries < 8) {
        setTimeout(tick, 250);
      }
    };

    const timer = setTimeout(tick, 150);
    return () => clearTimeout(timer);

    handleChapterClick(seconds);

  }, [plotId, location.search, iframeLoaded]);

  // Scroll to top of help page whenever the plot id changes
  useLayoutEffect(() => {
    const el = document.querySelector(".page-content");
    if (el) el.scrollTo({ top: 0, left: 0, behavior: "auto" });
    else window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [plotId]);


  return (
    <div
      className="page-content"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col lg={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-2">
                    <li className="breadcrumb-item">
                      <Button
                        color="link"
                        className="p-0 text-decoration-none"
                        onClick={() => navigate("/help")}
                        style={{ color: "#059669" }}
                      >
                        Help
                      </Button>
                    </li>
                    {/* Show parent breadcrumb if this is a sub-page */}
                    {parentData && (
                      <li className="breadcrumb-item">
                        <span style={{ color: "#6b7280" }}>
                          {parentData.title}
                        </span>
                      </li>
                    )}
                    <li
                      className="breadcrumb-item active"
                      style={{ color: "#374151" }}
                    >
                      {plotData.title}
                    </li>
                  </ol>
                </nav>
                <h1
                  className="help_header mb-0 fw-bold"
                  style={{ color: "#374151" }}
                >
                  {plotData.title}
                </h1>
                {/* Show parent context if this is a sub-page */}
                {parentData && (
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    Part of {parentData.title}
                  </p>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Video Section */}
        {!!plotData?.videoUrl?.trim() && (
          <Row className="mb-4">
            <Col lg={12}>
              <Row className="g-3 align-items-start">
                {/* Help video on LHS */}
                <Col xs={12} lg={8}>
                    <div
                      className="ratio ratio-16x9 mx-auto"
                      style={{ maxWidth: "800px" }}
                    >
                      <iframe
                        className="help_video"
                        id="help-video-iframe"
                        ref={iframeRef}
                        src={videoSrc}
                        onLoad={() => setIframeLoaded(true)}
                        key={`${plotId}:${videoSrc}`}
                        title={plotData.title}
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          border: "none",
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                </Col>

                {/* Video chapters section on RHS */}
                {chapters.length > 0 && (
                  <Col xs={12} lg={4}>
                    <div className="mx-auto mt-3" style={{ maxWidth: "800px" }}>
                      <Card style={{ border: "1px solid #e5e7eb" }}>
                        <CardBody style={{ padding: "16px 20px" }}>
                          <div className="fw-bold mb-2" style={{ color: "#374151" }}>
                            Chapters (Click to seek)
                          </div>
                          <ul className="list-unstyled mb-0">
                            {chapters.map((c) => (
                              <li key={`${c.time}-${c.label}`} className="mb-1">
                                <button
                                  type="button"
                                  className="chapter-link"
                                  onClick={() => handleChapterClick(c.seconds)}
                                >
                                  <span className="chapter-time">{c.time}</span>{" "}
                                  {c.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </CardBody>
                      </Card>
                    </div>
                  </Col>
                )}

              </Row>
            </Col>
          </Row>
        )}
        

        {/* Plot Description Section */}
        {plotData.plotDescription && (
          <Row>
            <Col lg={12}>
              <Card style={{ border: "1px solid #e5e7eb" }}>
                <CardBody style={{ padding: "24px" }}>
                  <div
                    className="plot_description help-content"
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#374151",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: plotData.plotDescription,
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Related plots Section - Show other sub-pages if this is part of a group */}
        {parentData &&
          parentData.subPages &&
          parentData.subPages.length > 1 && (
            <Row className="mt-4">
              <Col lg={12}>
                <Card style={{ border: "1px solid #e5e7eb" }}>
                  <CardBody style={{ padding: "24px" }}>
                    <h5 className="mb-3 fw-bold" style={{ color: "#374151" }}>
                      Related Help Topics in {parentData.title}
                    </h5>
                    <Row>
                      {parentData.subPages
                        .filter((sub) => sub.id !== plotId) // Exclude current plot
                        .map((relatedplot) => (
                          <Col md={6} key={relatedplot.id} className="mb-3">
                            <Card
                              className="h-100 position-relative overflow-hidden"
                              style={{
                                border: "2px solid #e5e7eb",
                                borderRadius: "12px",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                              }}
                              onClick={() =>
                                navigate(`/help/plot/${relatedplot.id}`)
                              }
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = "#059669";
                                e.currentTarget.style.transform =
                                  "translateY(-2px)";
                                e.currentTarget.style.boxShadow =
                                  "0 4px 12px rgba(5, 150, 105, 0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "#e5e7eb";
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <CardBody className="p-3">
                                <div className="d-flex align-items-start">
                                  <div
                                    className="flex-shrink-0 me-3"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      backgroundImage: `url(${relatedplot.image})`,
                                      backgroundSize: "contain",
                                      backgroundPosition: "center",
                                      backgroundRepeat: "no-repeat",
                                      backgroundColor: "#f8f9fa",
                                      borderRadius: "8px",
                                    }}
                                  />
                                  <div className="flex-grow-1">
                                    <h6
                                      className="fw-bold mb-2"
                                      style={{
                                        fontSize: "14px",
                                        color: "#374151",
                                        lineHeight: "1.3",
                                      }}
                                    >
                                      {relatedplot.title}
                                    </h6>
                                    <p
                                      className="mb-0 text-muted"
                                      style={{
                                        fontSize: "12px",
                                        lineHeight: "1.4",
                                        overflow: "hidden",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                      }}
                                    >
                                      {relatedplot.description}
                                    </p>
                                  </div>
                                  <div
                                    className="flex-shrink-0 ms-2"
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      backgroundColor: "#059669",
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <i
                                      className="ri-play-fill text-white"
                                      style={{
                                        fontSize: "12px",
                                        marginLeft: "1px",
                                      }}
                                    ></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
      </Container>

      {/* Custom Styles for Help Content */}
      <style>{`
        .help_header {
          font-size: 2rem;
          font-weight: bold;
          color: #374151;
          margin-bottom: 1rem;
        }

        .help_subheader {
          font-size: 1.25rem;
          font-weight: 600;
          color: #059669;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }


        .help-content h1,
        .help-content h2,
        .help-content h3,
        .help-content h4,
        .help-content h5,
        .help-content h6 {
          color: #059669;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }

        .help-content p {
          margin-bottom: 1rem;
        }

        .help-content ul,
        .help-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }

        .help-content li {
          margin-bottom: 0.5rem;
        }

        .help-content a {
          color: #059669;
          text-decoration: none;
        }

        .help-content a:hover {
          text-decoration: underline;
        }

        .help_table,
        .help-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }

        .help_table td,
        .help_table th,
        .help-content table td,
        .help-content table th {
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          vertical-align: top;
        }

        .help_table th,
        .help-content table th {
          background-color: #f8f9fa;
          font-weight: 600;
        }

        .help_table td:first-child,
        .help-content table td:first-child {
          background-color: #f8f9fa;
          font-weight: 600;
          width: 200px;
        }

        .help-content strong,
        .help-content b {
          font-weight: 600;
          color: #374151;
        }

        .help_plot {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 8px;
        }

        .plot_description {
          font-size: 14px;
          line-height: 1.6;
          color: #374151;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
          color: #6b7280;
        }

        .chapter-link {
          appearance: none;
          border: none;
          background: transparent;
          padding: 0;
          color: #059669;
          font-weight: 600;
          cursor: pointer;
          text-align: left;
        }

        .chapter-link:hover {
          text-decoration: underline;
        }

        .chapter-time {
          font-variant-numeric: tabular-nums;
          margin-right: 6px;
        }

      `}</style>
    </div>
  );
};

export default plotHelp;
